import { apiFetch } from './client.js';

// #478 — the dual-controlled re-vet an accepted company-profile change opens.
//
// Three acts by two people. The requested value lives on the change request,
// not on the case, until the third one — which is why this client fetches the
// values to verify from the server rather than reading them off the case the
// analyst is looking at. The case does not hold them yet, and that is the rule
// rather than a gap.

// #768 — and the LOGO re-vet: on an approved registration, a newly fetched
// call logo that differs from the one CTIA is delivered (bytes or URL). Same
// three acts, same two people; the analyst's completion is the SHAFT re-review
// of the candidate beside the delivered logo, and the approval is what makes
// the candidate the logo CTIA is sent.

/** Which kind of re-vet: #478's company-profile change, or #768's logo change. */
export type RevetKind = 'profile' | 'logo';

/** A logo snapshot as the re-vet shows it. The image is read by id (api/logoSnapshots.ts). */
export interface RevetLogoSide {
  snapshotId: number;
  sourceUrl: string;
  /** Null while a customer-requested logo is still being fetched, or after its fetch failed. */
  sha256: string | null;
  contentType: string | null;
  fetchedAt: string | null;
  trigger: string;
  outcome: 'pending' | 'ok' | 'failed';
  failureReason: string | null;
}

export interface RevetChangeState {
  caseId: string;
  kind: RevetKind;
  /** The customer request behind it; null for a logo change nobody asked for. */
  changeRequestId: string | null;
  /** Which fields this re-vet covers: legalName and/or businessAddress, or callLogoUrl. */
  fields: string[];
  /** `users.id`, not the Keycloak sub — compare against `user.serverId`. */
  completedBy: string | null;
  completedAt: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  revetStatus: string | null;
}

export interface OpenRevetChange extends RevetChangeState {
  /**
   * The completer's display name, joined server-side from `users`.
   *
   * The GET carries it and the three POSTs do not, which is why it lives here
   * rather than on `RevetChangeState`: the panel reloads through the GET after
   * every act, and this is the line the approver reads before countersigning.
   * Null when nobody has completed the re-vet yet.
   */
  completedByName: string | null;
  /** What the customer asked for, read off the change request. */
  requested: Record<string, unknown>;
  /**
   * What the case holds today, so both sides are on one screen. For a logo
   * re-vet, `callLogoUrl` is the delivered logo's URL.
   */
  current: {
    legalName?: string;
    businessAddress?: Record<string, unknown>;
    callLogoUrl?: string | null;
  };
  /** #768 — logo kind only: the new snapshot under review. */
  candidate?: RevetLogoSide | null;
  /** #768 — logo kind only: the snapshot CTIA is delivered today (null if none was ever reviewed). */
  delivered?: RevetLogoSide | null;
}

/** The open re-vet on this case, or null if there is none. */
export async function getRevetChange(caseId: string): Promise<OpenRevetChange | null> {
  return apiFetch<OpenRevetChange | null>(`/cases/${caseId}/revet-change`);
}

/**
 * Record that you verified the requested value against evidence.
 *
 * The note is required by the server as well as by this signature: it is all
 * the approver has to go on, and a sign-off on an empty one is a sign-off on
 * nothing. Writes nothing to the case.
 */
export async function completeRevetChange(
  caseId: string,
  evidenceNote: string,
): Promise<RevetChangeState> {
  return apiFetch<RevetChangeState>(`/cases/${caseId}/revet-change/complete`, {
    method: 'POST',
    body: JSON.stringify({ evidenceNote }),
  });
}

export interface RevetApproval extends RevetChangeState {
  /** The fields the approval actually wrote. */
  written: string[];
  syncQueued: boolean;
  /** Set when the approval committed but the sync job could not be queued. */
  warning: string | null;
  /** #768 — a newer logo change was waiting, and a logo re-vet opened on it. */
  nextLogoRevetOpened?: boolean;
}

/**
 * Sign it off. THIS is the act that writes the registration.
 *
 * Refused with 422 if you are the person who completed it — by the database,
 * not by this client, so a stale bundle cannot talk its way past it.
 */
export async function approveRevetChange(
  caseId: string,
  note?: string,
): Promise<RevetApproval> {
  return apiFetch<RevetApproval>(`/cases/${caseId}/revet-change/approve`, {
    method: 'POST',
    body: JSON.stringify(note && note.length > 0 ? { note } : {}),
  });
}

/**
 * Refuse the sign-off. Nothing is rolled back because nothing was written; the
 * re-vet goes back to the analyst carrying the reason, which is why it is
 * required.
 */
export async function declineRevetChange(
  caseId: string,
  reason: string,
): Promise<RevetChangeState> {
  return apiFetch<RevetChangeState>(`/cases/${caseId}/revet-change/decline`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export interface RevetWithdrawal extends RevetChangeState {
  /** True when a customer's request opened the re-vet and is now REJECTED with the reason. */
  changeRequestRejected: boolean;
  /** A logo change that waited behind this one opened its own re-vet. */
  nextLogoRevetOpened: boolean;
}

/**
 * #814 — close an open LOGO re-vet without approving it: a requested URL that
 * never fetches, or a candidate that keeps being declined. CTIA keeps the
 * delivered logo. A customer's request behind it is refused with the reason,
 * which is why it is required. Approver or admin; refused once an analyst has
 * completed the re-vet (approve or decline that first).
 */
export async function withdrawRevetChange(
  caseId: string,
  reason: string,
): Promise<RevetWithdrawal> {
  return apiFetch<RevetWithdrawal>(`/cases/${caseId}/revet-change/withdraw`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}
